/*
 * The Creative Analysis page (plan 2026-07-22-001, U7) — replaces the old
 * adset-grain Performance page UI at the SAME /performance route (bookmarks
 * live; the backend /performance/* routes are untouched, R7).
 *
 * The page is deliberately thin: it owns the product switcher (including the
 * ?product=all combined view, which fetches every product's blob in parallel
 * and lets the explorer's buildProducts synthesise the £→$ @1.27 combined
 * product), the R6 degraded states, the URL round-trip, and the recompute
 * flow — the explorer module (src/coverage, U6) owns every pixel inside its
 * container.
 *
 *   - Data: GET /coverage lists every product + its latest snapshot summary
 *     (null snapshot fields = no snapshot yet); GET /coverage/{product_id}
 *     serves the full explorer blob; a 404 there IS the empty state (the U5
 *     no-snapshot contract), never an error toast.
 *   - Degraded states: (a) no snapshot → EmptyState (+ operator recompute
 *     hint); (b) snapshot computed >36h ago → staleness banner; (c) ad-level
 *     pipeline lag (meta.stale_days ≥ 2) → its own banner, copy distinct from
 *     (b); (d) no master sheet → live-only info banner.
 *   - URL state: ?product/view/r/c/f/v mirror the explorer's shareable
 *     ViewState (written with replace:, so browsing history stays sane).
 *     Switching products RESETS the filter params (each product has its own
 *     dimension vocabulary — a carried filter silently empties the view) but
 *     preserves the chart/grid toggle and pivot dims. Row search is
 *     module-local and resets with the remount.
 *   - Loading: the snapshot query is keyed per product, so a switch always
 *     renders the skeleton until the NEW product's blob arrives — the
 *     previous product's data is never shown under the new label.
 *   - Recompute (operator): POST /coverage/sync → 202 → toast + disable the
 *     button + poll GET /coverage every 15s until the product's computed_at
 *     is strictly NEWER than the moment the job was queued (then refetch the
 *     snapshot) or a 10-minute timeout lapses. The newer-than gate (not a
 *     mere baseline diff) means an unrelated advance that predates the click
 *     can never end the watch early.
 *   - Honesty channel: meta.unmatched_spend_pct ≥ 5% (or meta.degraded) →
 *     partial-coverage warning; meta.warnings → muted footer block — so
 *     silent coverage loss is visible on the page.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Grid,
  Group,
  Paper,
  SegmentedControl,
  Select,
  Skeleton,
  Stack,
  Table as MantineTable,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Alert, Button, EmptyState, PageHeader } from "../components/ui";
import { useAuth } from "../auth/AuthContext";
import {
  queryKeys,
  useCoverageBrief,
  useCoverageList,
  useCoverageSnapshot,
  useCoverageSyncNow,
  useCoverageSyncStatus,
} from "../api/queries";
import { whatToMakeNext, type WhitespaceSuggestion } from "../coverage/whatnext";
import { getCoverageSnapshot } from "../api/endpoints";
import { errorMessage } from "../api/errors";
import { ApiError } from "../api/client";
import { mountExplorer } from "../coverage/explorer";
import { Overview, OverviewCards } from "../coverage/Overview";
import { ReportsRail } from "../coverage/ReportsRail";
import { applyReportState, stateFromParams, type ReportState } from "../coverage/report-state";
import {
  availableWindows,
  parseSegment,
  parseWindow,
  productForWindow,
  WINDOWS,
  type Segment,
  type WindowDays,
} from "../coverage/overview-model";
import type {
  CoverageDimId,
  CoverageProduct,
  CoverageProducts,
  ViewState,
} from "../coverage/types";
import { formatDateTime } from "../lib/format";

/** Snapshot age beyond which the generic staleness banner shows (R6). */
const STALE_SNAPSHOT_MS = 36 * 60 * 60 * 1000;
/** meta.stale_days at/above which the pipeline-lag banner shows (R6). */
const PIPELINE_LAG_DAYS = 2;
/** FunnelFlux tracked cost below this % of Meta spend gets a warning (the
 *  standing "flag Meta vs FunnelFlux gaps over ~15%" rule; mirrors the sync). */
const FF_COVERAGE_ALERT_PCT = 85;

/** meta.unmatched_spend_pct at/above which the partial-coverage alert shows. */
const UNMATCHED_ALERT_PCT = 5;
/** List-poll cadence while a recompute is in flight. */
const RECOMPUTE_POLL_MS = 15_000;
/** Give up waiting for a queued recompute after this long. */
const RECOMPUTE_TIMEOUT_MS = 10 * 60 * 1000;
/** The virtual combined-view id (?product=all). The explorer builds the
 *  combined product client-side from every fetched blob (£→$ @1.27). */
const ALL_ID = "all";

/** Quick-range presets for the recompute date range (2026-08-03). */
export const RANGE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 days" },
  { value: "last14", label: "Last 14 days" },
  { value: "last30", label: "Last 30 days" },
  { value: "thisMonth", label: "This month" },
  { value: "lastMonth", label: "Last month" },
];

/**
 * Resolve a quick-range preset to LOCAL calendar dates (YYYY-MM-DD, both
 * inclusive — the backend's own bounds semantics). "Last N days" includes
 * today; "Last month" is the full previous calendar month. Pure (injectable
 * `now`) so month/leap boundaries are unit-testable.
 */
export function presetRange(
  preset: string,
  now: Date = new Date(),
): { start: string; end: string } | null {
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const day = (offset: number) =>
    new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
  switch (preset) {
    case "today":
      return { start: iso(day(0)), end: iso(day(0)) };
    case "yesterday":
      return { start: iso(day(-1)), end: iso(day(-1)) };
    case "last7":
      return { start: iso(day(-6)), end: iso(day(0)) };
    case "last14":
      return { start: iso(day(-13)), end: iso(day(0)) };
    case "last30":
      return { start: iso(day(-29)), end: iso(day(0)) };
    case "thisMonth":
      return { start: iso(new Date(now.getFullYear(), now.getMonth(), 1)), end: iso(day(0)) };
    case "lastMonth":
      return {
        start: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        end: iso(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    default:
      return null;
  }
}

/**
 * Parse the shareable explorer state out of the URL. Values are passed raw —
 * the module validates each field itself (unknown dims fall back to defaults,
 * mirroring the reference's tolerant hash parsing), so a hand-edited URL can
 * never crash the mount.
 */
function parseViewState(params: URLSearchParams): Partial<ViewState> {
  const out: Partial<ViewState> = {};
  const view = params.get("view");
  if (view === "chart" || view === "grid") out.view = view;
  const r = params.get("r");
  if (r) out.rowDim = r as CoverageDimId;
  const c = params.get("c");
  if (c) out.colDim = c as CoverageDimId;
  const f = params.get("f");
  if (f) {
    out.filtDim = f as CoverageDimId;
    out.filtVal = params.get("v") ?? "";
  }
  // 2026-10-28 (saved reports): metrics, sort and the retargeting setting.
  const m = params.get("m");
  if (m) out.metrics = m.split(",").filter(Boolean);
  const so = params.get("s");
  if (so) out.sort = so;
  const rs = params.get("rs");
  if (rs === "all" || rs === "pros" || rs === "ret") out.segment = rs;
  return out;
}

/**
 * Light shape gate over the jsonb-passthrough payload before it reaches the
 * explorer (which would otherwise throw mid-render on a malformed blob). The
 * full record-level contract is pinned by the shared fixture + U8's sweep —
 * this only rejects a blob that isn't even product-shaped.
 */
function isCoverageProduct(v: unknown): v is CoverageProduct {
  if (v === null || typeof v !== "object" || Array.isArray(v)) return false;
  const p = v as Record<string, unknown>;
  return (
    typeof p.label === "string" &&
    typeof p.cur === "string" &&
    p.base !== null &&
    typeof p.base === "object" &&
    p.meta !== null &&
    typeof p.meta === "object" &&
    Array.isArray(p.sheet) &&
    Array.isArray(p.live)
  );
}

export function CreativeAnalysisPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  // Always-current params for callbacks/effects without re-running them on
  // every URL write (searchParams changes identity on each set).
  const paramsRef = useRef(searchParams);
  paramsRef.current = searchParams;

  // In-flight recompute: which product we watch and when the job was queued —
  // the completion gate (computed_at strictly newer than startedAt) AND the
  // give-up timeout both hang off that timestamp.
  const [recompute, setRecompute] = useState<{
    productId: string;
    startedAt: number;
  } | null>(null);
  const recomputing = recompute !== null;

  const listQuery = useCoverageList({
    refetchInterval: recomputing ? RECOMPUTE_POLL_MS : false,
  });
  const products = useMemo(
    () => listQuery.data?.products ?? [],
    [listQuery.data],
  );

  // Products that actually have a snapshot — the combined view needs ≥2.
  const withSnapshots = useMemo(
    () => products.filter((p) => p.snapshot_date !== null),
    [products],
  );
  const allAvailable = withSnapshots.length > 1;

  // Selected product: ?product= when it names a real product (or "all" for
  // the combined view when available), else the first from the list (the
  // plan's absent/invalid rule). Pure derivation — the URL is the single
  // source of truth for the selection.
  const urlProduct = searchParams.get("product") ?? "";
  const isAll = urlProduct === ALL_ID && allAvailable;
  const selected = isAll
    ? undefined
    : (products.find((p) => p.product_id === urlProduct) ?? products[0]);
  const selectedId = selected?.product_id ?? "";
  const hasSnapshot = selected != null && selected.snapshot_date !== null;

  const snapshotQuery = useCoverageSnapshot(selectedId, {
    enabled: !isAll && selectedId.length > 0 && hasSnapshot,
  });
  const snapshot = snapshotQuery.data;
  const snapshot404 =
    snapshotQuery.error instanceof ApiError && snapshotQuery.error.status === 404;

  const productData = useMemo(() => {
    if (!snapshot) return null;
    const p: unknown = snapshot.payload;
    return isCoverageProduct(p) ? p : null;
  }, [snapshot]);
  const malformed = snapshot != null && productData === null;

  /* ---- "What to make next" (whitespace scaffolder, 2026-07-29) ---------- */

  const suggestions = useMemo(
    () => (productData ? whatToMakeNext(productData) : []),
    [productData],
  );
  const brief = useCoverageBrief();
  const [draftingKey, setDraftingKey] = useState<string | null>(null);

  function draftTicket(s: WhitespaceSuggestion) {
    if (!selected) return;
    const key = `${s.angle}|${s.avatar}|${s.awareness}`;
    setDraftingKey(key);
    brief.mutate(
      {
        product_id: selected.product_id,
        angle: s.angle,
        avatar: s.avatar,
        awareness: s.awareness,
        note: s.reason,
      },
      {
        onSuccess: (r) => {
          setDraftingKey(null);
          notifications.show({
            title: "Draft ticket created",
            message: "It's in Ideation — rename, brief it properly, or delete it.",
            color: "green",
          });
          if (r.url) window.open(r.url, "_blank", "noopener");
        },
        onError: (e) => {
          setDraftingKey(null);
          notifications.show({
            title: "Couldn't create the draft",
            message: errorMessage(e),
            color: "red",
          });
        },
      },
    );
  }

  /* ---- Combined "All products" data (every blob, fetched in parallel) --- */

  // Same key/options as useCoverageSnapshot, so single-product and combined
  // views share the cache — switching to All reuses any blob already loaded.
  const allQueries = useQueries({
    queries: withSnapshots.map((p) => ({
      queryKey: queryKeys.coverageSnapshot(p.product_id),
      queryFn: () => getCoverageSnapshot(p.product_id),
      enabled: isAll,
      retry: false,
      staleTime: 5 * 60_000,
      gcTime: 10 * 60_000,
    })),
  });
  const allReady =
    isAll &&
    allQueries.length > 0 &&
    allQueries.every((q) => q.isSuccess || q.isError);
  // Valid product blobs among the settled queries (404s and malformed
  // payloads drop out; the combined view needs at least two to be honest).
  const allValidCount = allReady
    ? allQueries.filter((q) => q.data != null && isCoverageProduct(q.data.payload)).length
    : 0;
  // Content-keyed signature so the mount effect re-runs only when a blob
  // actually changes — the useQueries result array is a fresh identity every
  // render and must not be an effect dependency itself.
  const allKey = allReady
    ? allQueries
        .map((q) => (q.data != null ? `${q.data.product_id}@${q.data.computed_at}` : "x"))
        .join("|")
    : "";
  const allQueriesRef = useRef(allQueries);
  allQueriesRef.current = allQueries;

  /* ---- URL round-trip (the module never touches the URL itself) -------- */

  const applyState = useCallback(
    (s: ViewState) => {
      const next = new URLSearchParams(paramsRef.current);
      next.set("product", s.product);
      next.set("view", s.view);
      next.set("r", s.rowDim);
      next.set("c", s.colDim);
      if (s.filtDim) {
        next.set("f", s.filtDim);
        if (s.filtVal) next.set("v", s.filtVal);
        else next.delete("v");
      } else {
        next.delete("f");
        next.delete("v");
      }
      const m = (s.metrics ?? []).join(",");
      if (m && m !== "sp,pu,roas") next.set("m", m);
      else next.delete("m");
      if (s.sort && s.sort !== "sp") next.set("s", s.sort);
      else next.delete("s");
      if (s.segment && s.segment !== "all") next.set("rs", s.segment);
      else next.delete("rs");
      setSearchParams(next, { replace: true });
    },
    [setSearchParams],
  );

  /* ---- Overview tab, window and segment (2026-10-28) -------------------- */

  const tab = searchParams.get("tab") === "explore" ? "explore" : "overview";
  const segment = parseSegment(searchParams.get("seg"));
  // Windows every product in view can show: 28 always, 3/7/14 when the
  // snapshot carries them (older snapshots and operator ranges don't).
  const windowChoices = useMemo<WindowDays[]>(() => {
    if (!isAll) return productData ? availableWindows(productData) : [28];
    const sets = allQueries
      .map((q) => (q.data != null && isCoverageProduct(q.data.payload) ? availableWindows(q.data.payload) : null))
      .filter((x): x is WindowDays[] => x !== null);
    return sets.length ? WINDOWS.filter((w) => sets.every((ws) => ws.includes(w))) : [28];
    // allKey stands in for the allQueries identity (fresh every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAll, productData, allKey]);
  const requestedWindow = parseWindow(searchParams.get("w"));
  const win: WindowDays = windowChoices.includes(requestedWindow) ? requestedWindow : 28;
  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(paramsRef.current);
      if (value === null) next.delete(key);
      else next.set(key, value);
      setSearchParams(next, { replace: true });
    },
    [setSearchParams],
  );
  const openExploreByConcept = useCallback(() => {
    const next = new URLSearchParams(paramsRef.current);
    next.set("tab", "explore");
    next.set("view", "chart");
    next.set("r", "concept");
    setSearchParams(next, { replace: true });
  }, [setSearchParams]);

  const reportState: ReportState = stateFromParams(searchParams);
  const openReport = useCallback(
    (state: ReportState) => setSearchParams(applyReportState(paramsRef.current, state), { replace: true }),
    [setSearchParams],
  );
  const reportLink = useCallback(
    (state: ReportState) => {
      const params = applyReportState(paramsRef.current, state);
      return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    },
    [],
  );

  const currentValue = isAll ? ALL_ID : selectedId;
  const switchProduct = useCallback(
    (id: string) => {
      if (!id || id === currentValue) return;
      const next = new URLSearchParams(paramsRef.current);
      next.set("product", id);
      // Filters reset on switch — the remounted explorer reads its initial
      // state from these params, so deleting f/v IS the reset. view/r/c stay.
      next.delete("f");
      next.delete("v");
      setSearchParams(next, { replace: true });
    },
    [currentValue, setSearchParams],
  );

  /* ---- Explorer mount --------------------------------------------------- */

  const containerRef = useRef<HTMLDivElement | null>(null);

  // Single-product mount.
  useEffect(() => {
    const el = containerRef.current;
    if (isAll || !el || !snapshot || !productData) return;
    const handle = mountExplorer(
      el,
      { [snapshot.product_id]: productForWindow(productData, win) },
      {
        initialState: {
          ...parseViewState(paramsRef.current),
          product: snapshot.product_id,
        },
        onStateChange: applyState,
      },
    );
    return () => handle.destroy();
  }, [isAll, snapshot, productData, applyState, win, tab]);

  // Combined mount: every valid blob goes in; the explorer's buildProducts
  // synthesises the "all" product (£→$ @1.27) and the view opens on it.
  useEffect(() => {
    const el = containerRef.current;
    if (!isAll || !allReady || !el) return;
    const map: CoverageProducts = {};
    for (const q of allQueriesRef.current) {
      const d = q.data;
      if (d != null && isCoverageProduct(d.payload)) map[d.product_id] = productForWindow(d.payload, win);
    }
    if (Object.keys(map).length < 2) return; // the render shows the alert
    const handle = mountExplorer(el, map, {
      initialState: {
        ...parseViewState(paramsRef.current),
        product: ALL_ID,
      },
      onStateChange: applyState,
    });
    return () => handle.destroy();
    // allKey is the content signature for the queries snapshot in the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAll, allReady, allKey, applyState, win, tab]);

  /* ---- Recompute flow ---------------------------------------------------- */

  const syncNow = useCoverageSyncNow();

  // 2026-08-18: live sync status. Before this, a SLOW recompute and a
  // silently FAILED one were indistinguishable — the stale banner just sat
  // there while the operator clicked Recompute again and again. Polls fast
  // while anything is in flight (including a sync someone ELSE queued).
  const syncStatusQuery = useCoverageSyncStatus(
    isAll ? undefined : selectedId || undefined,
    {
      enabled: isAll || selectedId.length > 0,
      refetchInterval: (query) => {
        const st = query.state.data?.state;
        return recomputing || st === "running" || st === "queued" ? 4_000 : false;
      },
    },
  );
  const syncState = syncStatusQuery.data?.state ?? "idle";
  const syncBusy = syncState === "running" || syncState === "queued";
  const syncFailed = syncState === "failed";

  // A failed job ends the local watch immediately (instead of the 10-minute
  // give-up) and says WHY — Recompute doubles as Retry server-side.
  useEffect(() => {
    if (!recompute || !syncFailed) return;
    setRecompute(null);
    notifications.show({
      title: "Recompute FAILED",
      message: `${syncStatusQuery.data?.error ?? "unknown error"} — Recompute now retries it.`,
      color: "red",
      autoClose: false,
    });
  }, [recompute, syncFailed, syncStatusQuery.data]);

  // Optional operator date range (2026-07-26): the recompute — and therefore
  // the whole page — reflects this live window. Blank = the default trailing
  // window; the nightly sync resets to the default each morning.
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const rangeComplete = rangeStart !== "" && rangeEnd !== "";
  const rangeInvalid =
    (rangeStart !== "") !== (rangeEnd !== "") ||
    (rangeComplete && rangeStart > rangeEnd);

  // Quick-range preset (2026-08-03): picking one fills From/To; editing either
  // date by hand clears the preset (the dates are then a custom range).
  const [rangePreset, setRangePreset] = useState<string | null>(null);
  function applyPreset(preset: string | null) {
    setRangePreset(preset);
    const r = preset ? presetRange(preset) : null;
    setRangeStart(r?.start ?? "");
    setRangeEnd(r?.end ?? "");
  }

  function queueRecompute() {
    if (!selected && !isAll) return;
    if (rangeInvalid) return;
    const targetId = isAll ? ALL_ID : selected!.product_id;
    // 2026-08-04 (fast recompute): a single-product page syncs ONLY that
    // product; the All-products view still recomputes the whole brand.
    syncNow.mutate(
      {
        ...(rangeComplete ? { start: rangeStart, end: rangeEnd } : {}),
        ...(isAll ? {} : { product_id: selected!.product_id }),
      },
      {
      onSuccess: () => {
        // Honest toast (2026-08-18): a click while a sync is already in
        // flight is ABSORBED by the server's dedup — say so instead of
        // pretending a fresh one started.
        notifications.show(
          syncBusy
            ? {
                title: "Already recomputing",
                message:
                  "A sync for this scope is in flight — your click joined it. The page updates automatically.",
                color: "blue",
              }
            : {
                title: "Recompute queued",
                message: "Takes a few minutes — the page updates automatically.",
                color: "blue",
              },
        );
        setRecompute({
          productId: targetId,
          startedAt: Date.now(),
        });
        void syncStatusQuery.refetch();
      },
    });
  }

  // Completion watcher: the queued job landed once the watched product's
  // computed_at is strictly NEWER than the moment the job was queued — then
  // pull the fresh blob. A click-time-baseline diff would misfire when an
  // unrelated advance (e.g. the daily sync) landed between the list fetch
  // that produced the baseline and the click; a stamp can only pass this gate
  // if it was written after the queue moment. (Date.parse of a malformed
  // stamp is NaN — every comparison fails, so the watch just keeps polling
  // until its timeout.)
  useEffect(() => {
    if (!recompute) return;
    // Combined view: the sync recomputes every product — the first stamp
    // written after the queue moment proves the job ran, so refresh all
    // cached blobs then (stragglers land via their own staleTime refetch).
    if (recompute.productId === ALL_ID) {
      const anyNewer = (listQuery.data?.products ?? []).some(
        (p) => p.computed_at !== null && Date.parse(p.computed_at) > recompute.startedAt,
      );
      if (anyNewer) {
        setRecompute(null);
        void queryClient.invalidateQueries({ queryKey: ["coverage", "snapshot"] });
      }
      return;
    }
    const watched = (listQuery.data?.products ?? []).find(
      (p) => p.product_id === recompute.productId,
    );
    const current = watched?.computed_at ?? null;
    if (current !== null && Date.parse(current) > recompute.startedAt) {
      setRecompute(null);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.coverageSnapshot(recompute.productId),
      });
    }
  }, [recompute, listQuery.data, queryClient]);

  // Give-up timeout: stop polling and re-enable the button after 10 minutes.
  useEffect(() => {
    if (!recompute) return;
    const remaining = RECOMPUTE_TIMEOUT_MS - (Date.now() - recompute.startedAt);
    const timer = window.setTimeout(
      () => setRecompute(null),
      Math.max(0, remaining),
    );
    return () => window.clearTimeout(timer);
  }, [recompute]);

  /* ---- Degraded-state facts (from the LIST summary — available even while
   *      the multi-MB payload is still in flight) --------------------------- */

  const meta = selected?.meta ?? null;
  const computedAt = selected?.computed_at ?? null;
  const snapshotStale =
    computedAt !== null &&
    Date.now() - Date.parse(computedAt) > STALE_SNAPSHOT_MS;
  const pipelineLagged = (meta?.stale_days ?? 0) >= PIPELINE_LAG_DAYS;
  // 2026-08-05 (the FF 5-vs-7 reconcile): the window anchors to AdManage's
  // ad-level data edge, which lags Meta by a day+ — when the edge is OLDER
  // than the day before the compute, recent whole days (incl. yesterday) are
  // invisible on the page while Ads Manager already shows them. stale_days
  // can't measure this in prod (no account-spend seam), but end-vs-compute
  // date CAN — so say it plainly instead of letting recomputes look broken.
  const dataGapDays = useMemo(() => {
    if (!meta?.end || computedAt === null) return 0;
    const c = new Date(computedAt);
    const dayBefore = new Date(
      Date.UTC(c.getUTCFullYear(), c.getUTCMonth(), c.getUTCDate() - 1),
    );
    const dayBeforeIso = dayBefore.toISOString().slice(0, 10);
    if (meta.end >= dayBeforeIso) return 0;
    return Math.round(
      (Date.parse(dayBeforeIso) - Date.parse(meta.end)) / 86_400_000,
    );
  }, [meta, computedAt]);
  const noSheet = meta?.no_sheet === true;
  // Honesty channel: unmatched spend + sync warnings ride the list summary;
  // `degraded` only rides the snapshot payload's meta (the summary schema
  // doesn't carry it), so it lights up once the blob arrives.
  const unmatchedPct = meta?.unmatched_spend_pct ?? 0;
  const degraded = productData?.meta.degraded === true;
  const coveragePartial = unmatchedPct >= UNMATCHED_ALERT_PCT || degraded;
  const syncWarnings = meta?.warnings ?? [];

  // Combined-view facts: any product tripping a gate lights the banner (the
  // per-product detail lives one segmented-control click away).
  const allStale =
    isAll &&
    withSnapshots.some(
      (p) =>
        p.computed_at !== null &&
        Date.now() - Date.parse(p.computed_at) > STALE_SNAPSHOT_MS,
    );
  const allMaxLag = isAll
    ? Math.max(0, ...withSnapshots.map((p) => p.meta?.stale_days ?? 0))
    : 0;
  const allMaxUnmatched = isAll
    ? Math.max(0, ...withSnapshots.map((p) => p.meta?.unmatched_spend_pct ?? 0))
    : 0;

  /* ---- Render ------------------------------------------------------------ */

  return (
    <>
      <PageHeader
        title="Creative Analysis"
        description="What's been produced vs what's running — coverage and payback by creative dimension, from the daily snapshot sync."
        actions={
          // Viewer-and-above (2026-07-29, was operator-only): the backend
          // route was opened to viewers so media buyers can pull a date
          // range from their phones.
          <Group gap="xs" wrap="wrap" align="flex-end">
              <Select
                size="xs"
                label="Quick range"
                aria-label="Quick range"
                placeholder="Pick a range…"
                w={140}
                data={RANGE_PRESETS}
                value={rangePreset}
                clearable
                onChange={applyPreset}
                comboboxProps={{ withinPortal: true }}
              />
              <TextInput
                type="date"
                size="xs"
                label="From"
                aria-label="Range start"
                value={rangeStart}
                error={rangeInvalid ? " " : undefined}
                onChange={(e) => {
                  setRangePreset(null); // hand-edited ⇒ custom range
                  setRangeStart(e.currentTarget.value);
                }}
              />
              <TextInput
                type="date"
                size="xs"
                label="To"
                aria-label="Range end"
                value={rangeEnd}
                error={rangeInvalid ? " " : undefined}
                onChange={(e) => {
                  setRangePreset(null);
                  setRangeEnd(e.currentTarget.value);
                }}
              />
              <Button
                variant="primary"
                onClick={queueRecompute}
                disabled={
                  recomputing ||
                  syncNow.isPending ||
                  rangeInvalid ||
                  (!selected && !isAll)
                }
              >
                {syncNow.isPending
                  ? "Queuing…"
                  : recomputing
                    ? "Recomputing…"
                    : rangeComplete
                      ? "Recompute range"
                      : "Recompute now"}
              </Button>
            </Group>
        }
      />
      <Text c="dimmed" fz="xs" mt={-8} mb="xs" ta="right">
        Blank dates = the default 28-day window · a range recompute applies to
        every product, changes what everyone sees, and resets with the nightly
        sync.
      </Text>

      {syncNow.isError && (
        <Box mb="md">
          <Alert tone="error">
            Couldn't queue the recompute: {errorMessage(syncNow.error)}
          </Alert>
        </Box>
      )}

      {listQuery.isError && (
        <Box mb="md">
          <Alert tone="error">
            Couldn't load coverage: {errorMessage(listQuery.error)}
          </Alert>
        </Box>
      )}

      {listQuery.isLoading ? (
        <EmptyState icon="◷" title="Loading coverage…" />
      ) : products.length === 0 ? (
        <EmptyState
          icon="◔"
          title="No products yet"
          description="Products appear here once they're configured for this brand — snapshots land with the daily coverage sync."
        />
      ) : (
        <>
          {/* Products overview (2026-08-24): the flat per-product scoreboard —
              spend / purchases / revenue / ROAS straight from each latest
              snapshot's meta totals, no ranking dims, no payload fetches.
              Rows computed before the totals existed show dashes until their
              next recompute. Click a row to open that product. */}
          <Box mb="md" style={{ overflowX: "auto" }}>
            <MantineTable
              striped
              highlightOnHover
              withTableBorder
              horizontalSpacing="sm"
              verticalSpacing={6}
              fz="sm"
              aria-label="Products overview"
            >
              <MantineTable.Thead>
                <MantineTable.Tr>
                  <MantineTable.Th>Product</MantineTable.Th>
                  <MantineTable.Th ta="right">Spend (Meta)</MantineTable.Th>
                  <MantineTable.Th ta="right">Conversions</MantineTable.Th>
                  <MantineTable.Th ta="right">Revenue</MantineTable.Th>
                  <MantineTable.Th ta="right">ROAS</MantineTable.Th>
                  <MantineTable.Th>Revenue source</MantineTable.Th>
                  <MantineTable.Th ta="right">Live ads</MantineTable.Th>
                  <MantineTable.Th>Window</MantineTable.Th>
                </MantineTable.Tr>
              </MantineTable.Thead>
              <MantineTable.Tbody>
                {products.map((p) => {
                  const m = p.meta;
                  const cur = m?.currency ?? "$";
                  const money = (v: number | null | undefined) =>
                    typeof v === "number" ? `${cur}${Math.round(v).toLocaleString()}` : "—";
                  // Revenue: FunnelFlux when the snapshot has it, else Meta —
                  // labelled per row, never mixed (2026-10-28).
                  const ff = m?.ff_status === "ok" && typeof m.window_ff_revenue === "number";
                  const revenue = ff ? m!.window_ff_revenue : m?.window_revenue;
                  const conversions = ff ? m!.window_ff_conversions : m?.window_purchases;
                  const roas =
                    typeof m?.window_spend === "number" &&
                    m.window_spend > 0 &&
                    typeof revenue === "number"
                      ? (revenue / m.window_spend).toFixed(2)
                      : "—";
                  return (
                    <MantineTable.Tr
                      key={p.product_id}
                      onClick={() => switchProduct(p.product_id)}
                      style={{ cursor: "pointer" }}
                      data-selected={p.product_id === selectedId && !isAll ? true : undefined}
                    >
                      <MantineTable.Td fw={p.product_id === selectedId && !isAll ? 700 : 400}>
                        {p.label || p.key}
                      </MantineTable.Td>
                      <MantineTable.Td ta="right">{money(m?.window_spend)}</MantineTable.Td>
                      <MantineTable.Td ta="right">
                        {typeof conversions === "number" ? Math.round(conversions).toLocaleString() : "—"}
                      </MantineTable.Td>
                      <MantineTable.Td ta="right">{money(revenue)}</MantineTable.Td>
                      <MantineTable.Td ta="right">{roas}</MantineTable.Td>
                      <MantineTable.Td c="dimmed" fz="xs">
                        {ff
                          ? `FunnelFlux${typeof m?.ff_cost_coverage_pct === "number" ? ` · ${Math.round(m.ff_cost_coverage_pct)}% of spend tracked` : ""}`
                          : m
                            ? "Meta-reported"
                            : "—"}
                      </MantineTable.Td>
                      <MantineTable.Td ta="right">{m?.live_rows ?? "—"}</MantineTable.Td>
                      <MantineTable.Td c="dimmed" fz="xs">
                        {m ? `${m.start} → ${m.end}` : "no snapshot"}
                      </MantineTable.Td>
                    </MantineTable.Tr>
                  );
                })}
              </MantineTable.Tbody>
            </MantineTable>
          </Box>

          {/* Horizontal scroller (2026-07-29): with several products + "All
              products" the switcher is wider than a phone — let it scroll
              instead of overflowing the page. */}
          <Box mb="md" style={{ overflowX: "auto" }} pb={4}>
            <SegmentedControl
              value={currentValue}
              onChange={switchProduct}
              data={[
                ...products.map((p) => ({
                  value: p.product_id,
                  label: p.label || p.key,
                })),
                ...(allAvailable
                  ? [{ value: ALL_ID, label: "All products" }]
                  : []),
              ]}
            />
          </Box>

          {/* Reports rail (2026-10-28) beside the controls and content. */}
          <Grid gap="md">
            <Grid.Col span={{ base: 12, md: 3 }}>
              <ReportsRail
                productId={isAll ? null : selectedId || null}
                current={reportState}
                onOpen={openReport}
                linkFor={reportLink}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 9 }} style={{ minWidth: 0 }}>
          {/* Overview / Explore + the window both tabs share (2026-10-28). */}
          <Group mb="md" gap="sm" wrap="wrap">
            <SegmentedControl
              value={tab}
              onChange={(v) => setParam("tab", v === "explore" ? "explore" : null)}
              data={[
                { value: "overview", label: "Overview" },
                { value: "explore", label: "Explore" },
              ]}
              aria-label="View"
            />
            <SegmentedControl
              value={String(win)}
              onChange={(v) => setParam("w", v === "28" ? null : v)}
              data={WINDOWS.map((w) => ({
                value: String(w),
                label: `${w} days`,
                disabled: !windowChoices.includes(w),
              }))}
              aria-label="Window"
            />
          </Group>

          {isAll ? (
            <Stack gap="sm">
              {allStale && (
                <Alert tone="warning">
                  One or more product snapshots are stale (computed more than
                  36h ago) — the combined view may be out of date.
                </Alert>
              )}
              {allMaxLag >= PIPELINE_LAG_DAYS && (
                <Alert tone="warning">
                  Ad-level data is delayed for at least one product (up to{" "}
                  {allMaxLag} day{allMaxLag === 1 ? "" : "s"} behind account
                  spend) — switch to a single product for the detail.
                </Alert>
              )}
              {allMaxUnmatched >= UNMATCHED_ALERT_PCT && (
                <Alert tone="warning">
                  Up to {Math.round(allMaxUnmatched)}% of a product's window
                  spend couldn't be matched to an ad name — combined coverage
                  is partial.
                </Alert>
              )}

              {allReady && allValidCount >= 2 ? (
                <>
                  {tab === "overview" && (
                    <OverviewCards
                      products={allQueries.flatMap((q) => {
                        const payload: unknown = q.data?.payload;
                        if (q.data == null || !isCoverageProduct(payload)) return [];
                        const id = q.data.product_id;
                        return [{
                          id,
                          label: products.find((p) => p.product_id === id)?.label ?? payload.label,
                          product: payload,
                        }];
                      })}
                      window={win}
                      segment={segment}
                      onSegment={(sv: Segment) => setParam("seg", sv === "pros" ? null : sv)}
                      onOpen={switchProduct}
                    />
                  )}
                  <div ref={containerRef} style={{ display: tab === "explore" ? undefined : "none" }} />
                </>
              ) : allReady ? (
                <Alert tone="error">
                  Couldn't load enough product snapshots for the combined view
                  — {allValidCount} of {withSnapshots.length} loaded. Try a
                  single product, or queue a recompute.
                </Alert>
              ) : (
                <Stack gap="sm" data-testid="coverage-loading" aria-hidden="true">
                  <Skeleton height={32} width="55%" />
                  <Skeleton height={140} />
                  <Skeleton height={140} />
                </Stack>
              )}
            </Stack>
          ) : !hasSnapshot || snapshot404 ? (
            <EmptyState
              icon="◔"
              title="No coverage snapshot yet"
              description='The daily sync hasn&apos;t produced a snapshot for this product yet — it lands by ~06:00 UK. Press "Recompute now" to queue one immediately.'
            />
          ) : (
            <Stack gap="sm">
              {syncBusy && (
                <Alert tone="info">
                  Recompute {syncState === "queued" ? "queued" : "running"}
                  {syncStatusQuery.data?.started_at
                    ? ` — started ${formatDateTime(syncStatusQuery.data.started_at)}`
                    : ""}
                  . The page refreshes itself when it lands; clicking Recompute
                  again won&apos;t start another.
                </Alert>
              )}
              {syncFailed && !recomputing && (
                <Alert tone="error">
                  The last recompute FAILED
                  {syncStatusQuery.data?.finished_at
                    ? ` (${formatDateTime(syncStatusQuery.data.finished_at)})`
                    : ""}
                  : {syncStatusQuery.data?.error ?? "unknown error"} —
                  &quot;Recompute now&quot; retries it.
                </Alert>
              )}
              {snapshotStale && !syncBusy && (
                <Alert tone="warning">
                  Snapshot is stale — computed {formatDateTime(computedAt)},
                  more than 36h ago. The daily sync may not be landing; the
                  data below may be out of date.
                </Alert>
              )}
              {pipelineLagged && meta && (
                <Alert tone="warning">
                  Ad-level data for this product is delayed ({meta.stale_days}{" "}
                  day{meta.stale_days === 1 ? "" : "s"} behind account spend);
                  windows are edge-anchored
                  {meta.ad_edge ? ` to ${meta.ad_edge}` : ""}.
                </Alert>
              )}
              {dataGapDays > 0 && meta && (
                <Alert tone="warning">
                  AdManage&apos;s ad-level data currently ends {meta.end} — the
                  most recent{" "}
                  {dataGapDays === 1
                    ? "day (yesterday) is"
                    : `${dataGapDays} days (including yesterday) are`}{" "}
                  not in this view yet. Ads Manager will show newer conversions
                  until the data catches up; recompute later and they&apos;ll
                  appear.
                </Alert>
              )}
              {noSheet && (
                <Alert tone="info">
                  No master sheet configured for this product — live data only.
                </Alert>
              )}
              {/* Revenue source (2026-10-28): spend is Meta, revenue is
                  FunnelFlux when connected — say plainly when it isn't. */}
              {productData?.meta.ff?.status === "unavailable" && (
                <Alert tone="warning">
                  FunnelFlux couldn&apos;t be read for this snapshot, so revenue
                  and ROAS are Meta-reported. Recompute later to retry.
                </Alert>
              )}
              {productData?.meta.ff?.status === "no_credential" && (
                <Alert tone="info">
                  This product has FunnelFlux funnels set, but the brand has no
                  FunnelFlux token yet, so revenue and ROAS are Meta-reported.
                  Add the token under Credentials.
                </Alert>
              )}
              {productData?.meta.ff?.status === "not_configured" && (
                <Alert tone="info">
                  Revenue and ROAS are Meta-reported. Add this product&apos;s
                  FunnelFlux funnel ids in its config to see FunnelFlux revenue.
                </Alert>
              )}
              {productData?.meta.ff?.status === "ok" &&
                typeof productData.meta.ff.cost_coverage_pct === "number" &&
                productData.meta.ff.cost_coverage_pct < FF_COVERAGE_ALERT_PCT && (
                  <Alert tone="warning">
                    FunnelFlux tracked only{" "}
                    {Math.round(productData.meta.ff.cost_coverage_pct)}% of this
                    window&apos;s Meta spend, so FunnelFlux revenue reads low.
                    Some ads&apos; links probably don&apos;t carry the ad id.
                  </Alert>
                )}
              {coveragePartial && (
                <Alert tone="warning">
                  {Math.round(unmatchedPct)}% of this window's spend couldn't
                  be matched to an ad name — coverage is partial.
                </Alert>
              )}

              {suggestions.length > 0 && (
                <Paper
                  withBorder
                  radius="lg"
                  p="md"
                  data-testid="whatnext-panel"
                >
                  <Group justify="space-between" mb="xs" wrap="nowrap">
                    <Text fw={600} fz="sm">
                      What to make next
                    </Text>
                    <Text c="dimmed" fz="xs">
                      pays above the cold average · little made recently
                    </Text>
                  </Group>
                  <Stack gap="xs">
                    {suggestions.map((s) => {
                      const key = `${s.angle}|${s.avatar}|${s.awareness}`;
                      return (
                        <Group
                          key={key}
                          justify="space-between"
                          wrap="wrap"
                          gap="xs"
                        >
                          <Box>
                            <Text fz="sm" fw={500}>
                              {s.angle} × {s.avatar} × {s.awareness}
                            </Text>
                            <Text c="dimmed" fz="xs">
                              {s.reason}
                            </Text>
                          </Box>
                          {can("operate") && (
                            <Button
                              size="sm"
                              variant="secondary"
                              loading={draftingKey === key}
                              disabled={
                                draftingKey !== null && draftingKey !== key
                              }
                              onClick={() => draftTicket(s)}
                            >
                              Draft ticket
                            </Button>
                          )}
                        </Group>
                      );
                    })}
                  </Stack>
                </Paper>
              )}

              {productData ? (
                <>
                  {tab === "overview" && (
                    <Overview
                      product={productData}
                      window={win}
                      segment={segment}
                      onSegment={(sv: Segment) => setParam("seg", sv === "pros" ? null : sv)}
                      onOpenFamilies={openExploreByConcept}
                    />
                  )}
                  <div ref={containerRef} style={{ display: tab === "explore" ? undefined : "none" }} />
                </>
              ) : malformed ? (
                <Alert tone="error">
                  This product's snapshot payload is malformed — queue a
                  recompute, or check the coverage-sync job.
                </Alert>
              ) : snapshotQuery.isError ? (
                <Alert tone="error">
                  Couldn't load the snapshot: {errorMessage(snapshotQuery.error)}
                </Alert>
              ) : (
                // Initial load AND product switch: the query is keyed per
                // product, so the previous product's blob is never rendered
                // under the new label — the skeleton holds the space instead.
                <Stack gap="sm" data-testid="coverage-loading" aria-hidden="true">
                  <Skeleton height={32} width="55%" />
                  <Skeleton height={140} />
                  <Skeleton height={140} />
                </Stack>
              )}

              {meta && (
                <Text c="dimmed" fz="xs">
                  Data through {meta.end}.
                </Text>
              )}
              {syncWarnings.length > 0 && (
                <Box data-testid="coverage-warnings">
                  <Text c="dimmed" fz="xs" fw={600}>
                    Sync warnings
                  </Text>
                  {syncWarnings.map((w) => (
                    <Text key={w} c="dimmed" fz="xs">
                      {w}
                    </Text>
                  ))}
                </Box>
              )}
            </Stack>
          )}
            </Grid.Col>
          </Grid>
        </>
      )}
    </>
  );
}
